import { AlarmClock, Check, Plus } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { formatLongDate, subjectOrPlaceholder } from "../mailFormat";
import { taskStatusOf } from "../mailTaskStatus";
import { withReturnLocation } from "../participantLinks";
import type { MailMessage, MailTask } from "../types";
import { MailPill } from "./MailPill";

const STATUS_TEXT = { done: "Completed", overdue: "Overdue", open: "Open" } as const;
const STATUS_TONE = { done: "completedTask", overdue: "overdueTask", open: "task" } as const;

const taskTitle = (task: MailTask, status: keyof typeof STATUS_TEXT): string => {
  const text = STATUS_TEXT[status];
  return task.dueDateUtc ? `${text} task, due ${formatLongDate(task.dueDateUtc)}` : `${text} task`;
};

/** A linked task as a pill showing its status, opening the task and returning here once it is saved. */
export const MailTaskPill = ({ task, now }: { task: MailTask; now: Date }) => {
  const status = taskStatusOf(task, now);
  return (
    <MailPill
      tone={STATUS_TONE[status]}
      href={withReturnLocation(task.url)}
      title={taskTitle(task, status)}
    >
      {status === "done" && <Check aria-label="Completed" className="mr-0.5 inline size-3 align-[-2px]" />}
      {status === "overdue" && <AlarmClock aria-label="Overdue" className="mr-0.5 inline size-3 align-[-2px]" />}
      {subjectOrPlaceholder(task.subject)}
    </MailPill>
  );
};

/**
 * The tasks linked to an email, whether created when it was imported or from the email, and
 * a link to create another. Both return to this email once the task is saved.
 */
export const MailTaskLine = ({ message }: { message: MailMessage }) => {
  const now = new Date();

  return (
    <div className="flex gap-2 text-sm">
      <span className="w-10 shrink-0 pt-px text-gray-500">Tasks</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-1">
        {message.tasks.map(task => <MailTaskPill key={task.id} task={task} now={now} />)}
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-xs text-sky-700 hover:bg-sky-50 hover:text-sky-800"
        >
          <a href={withReturnLocation(message.addTaskUrl)} title="Create a task from this email">
            <Plus /> Create task
          </a>
        </Button>
      </div>
    </div>
  );
};
